import {
  Box,
  Stack,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { AnimatePresence, motion } from "framer-motion";
import MotionContainer from "../../../../components/FramerMotion/MotionContainer";
import {
  AddButton,
  DeleteButton,
} from "../../../../components/mui/Buttons/Buttons";
import useResponsive from "../../../../Hooks/useResponsive";
import { isValidTimeRange } from "../../../../utils/constants/TimeHourValidation";

dayjs.extend(customParseFormat);

const TimeValidations = () => (
  <Box>
    <Alert severity="error" sx={{ mt: 1 }}>
      Invalid time Range..!
    </Alert>
  </Box>
);

const DayTimePreference = React.memo(({ setPreferences, preferences }) => {
  const smUp = useResponsive("up", "sm");
  const mdUp = useResponsive("up", "md");

  const weekdays = useMemo(
    () => [
      { name: "Sunday", value: 0 },
      { name: "Monday", value: 1 },
      { name: "Tuesday", value: 2 },
      { name: "Wednesday", value: 3 },
      { name: "Thursday", value: 4 },
      { name: "Friday", value: 5 },
      { name: "Saturday", value: 6 },
    ],
    []
  );

  const handleDayChange = useCallback(
    (index, event) => {
      setPreferences((prev) => {
        const updated = [...prev];
        updated[index].day = event.target.value;
        return updated;
      });
    },
    [setPreferences]
  );

  const handleTimeChange = useCallback(
    (index, field, newValue) => {
      setPreferences((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          [field]: newValue,
        };
        return updated;
      });
    },
    [setPreferences]
  );

  const handleAddPreference = useCallback(() => {
    setPreferences((prev) => [
      ...prev,
      {
        day: "",
        startTime: null,
        endTime: null,
      },
    ]);
  }, [setPreferences]);

  const handleRemovePreference = useCallback(
    (index) => {
      setPreferences((prev) => prev.filter((_, i) => i !== index));
    },
    [setPreferences]
  );

  return (
    <Box>
      <Stack direction="column" spacing={2}>
        <AnimatePresence>
          {preferences.map((pref, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              transition={{ duration: 0.5 }}
            >
              <Stack
                direction={mdUp ? "row" : "column"}
                spacing={1}
                alignItems="center"
              >
                <MotionContainer delay={1}>
                  <FormControl fullWidth>
                    <InputLabel id={`day-select-label-${index}`}>
                      Days
                    </InputLabel>
                    <Select
                      labelId={`day-select-label-${index}`}
                      value={pref.day}
                      label="Days"
                      required
                      onChange={(event) => handleDayChange(index, event)}
                    >
                      {weekdays.map((item) => (
                        <MenuItem key={item.value} value={item.name}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MotionContainer>

                {["startTime", "endTime"].map((field, i) => (
                  <MotionContainer key={field} delay={0.5 - i * 0.2}>
                    <FormControl fullWidth>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimePicker
                          label={
                            field === "startTime" ? "Start Time" : "End Time"
                          }
                          value={pref[field]}
                          onChange={(newValue) =>
                            handleTimeChange(index, field, newValue)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              error={!pref[field]} // If value is null, show error
                              helperText={
                                !pref[field] ? "Time is required" : ""
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </FormControl>
                  </MotionContainer>
                ))}

                <DeleteButton
                  size="small"
                  action={() => handleRemovePreference(index)}
                />
              </Stack>

              {pref.startTime !== null &&
                pref.endTime !== null &&
                !isValidTimeRange(pref.startTime, pref.endTime) && (
                  <MotionContainer delay={0.5}>
                    <TimeValidations />
                  </MotionContainer>
                )}
            </motion.div>
          ))}
        </AnimatePresence>
        <AddButton size="small" action={handleAddPreference}>
          Add Preference
        </AddButton>
      </Stack>
    </Box>
  );
});

export default DayTimePreference;
